import { Link } from "react-router-dom";
import {
  Award,
  ShieldCheck,
  Microscope,
  Activity,
  Users,
  HeartPulse,
  ArrowRight,
  CheckCircle2,
  GraduationCap,
  BadgeCheck,
  MessageCircle,
  Phone,
  Ear,
  Wind,
  Stethoscope,
} from "lucide-react";
import { DOCTOR, CONTACT, WHATSAPP_BASE, IMAGES, EQUIPMENT, WHY_CHOOSE, PATIENT_JOURNEY, GALLERY } from "@/data/site";
import { PROCEDURES } from "@/data/procedures";
import { SectionHeading, Eyebrow, CTABand } from "@/components/Shared";
import { Seo, breadcrumbSchema } from "@/components/Seo";

const WHY_ICONS = [Award, Users, ShieldCheck, Microscope, Activity, BadgeCheck];
const PROC_ICONS = {
  "Nose & Sinus": Wind,
  Ear: Ear,
  Throat: Activity,
  "Head & Neck": HeartPulse,
  "Emergency ENT": Stethoscope,
};

const bookText = "Hello Dr. Sanjay Teza, I would like to book a surgical consultation.";

export default function Home() {
  const featured = PROCEDURES.filter((p) => p.detailed);

  return (
    <div data-testid="home-page">
      <Seo
        title="Varanasi's Top ENT Surgeon — Dr. Sanjay Teza | Advanced ENT Surgery Centre"
        description="Dr. Sanjay Teza (MBBS, MS ENT) is a leading ENT surgeon in Varanasi with 25+ years of experience & 3,00,000+ patients treated. Advanced, minimally invasive ear, nose, throat, head & neck surgery. Book a consultation."
        path="/"
        schemas={[
          breadcrumbSchema([{ name: "Home", path: "/" }]),
        ]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:py-20">
          <div className="lg:col-span-6">
            <Eyebrow>Advanced ENT Surgery Centre · Varanasi</Eyebrow>
            <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-brand-ink sm:text-5xl lg:text-6xl">
              Varanasi's Top ENT Surgeon —{" "}
              <span className="text-brand-primary">Dr. Sanjay Teza</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-brand-slate md:text-lg">
              {DOCTOR.qualifications} · {DOCTOR.title}. Over{" "}
              {DOCTOR.experienceYears} years of experience and{" "}
              {DOCTOR.patientsTreated} patients treated — advanced, minimally
              invasive surgery for the ear, nose, throat, head and neck.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href={`${WHATSAPP_BASE}?text=${encodeURIComponent(bookText)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary px-7 py-4 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-brand-action"
              >
                <MessageCircle className="h-5 w-5" /> Book a Consultation
              </a>
              <Link
                to="/surgery-guide"
                data-testid="hero-explore-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-line bg-white px-7 py-4 text-sm font-semibold text-brand-ink transition-colors duration-200 hover:border-brand-primary hover:text-brand-primary"
              >
                Explore Surgeries <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-10 grid max-w-lg grid-cols-3 gap-4">
              {[
                { value: DOCTOR.experienceYears, label: "Years Experience" },
                { value: DOCTOR.patientsTreated, label: "Patients Treated" },
                { value: "10+", label: "ENT Procedures" },
              ].map((s) => (
                <div key={s.label} className="rounded-lg border border-brand-line bg-brand-bg p-4">
                  <div className="font-heading text-2xl font-extrabold text-brand-primary">
                    {s.value}
                  </div>
                  <div className="mt-1 text-xs font-medium text-brand-slate">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative">
              <div className="overflow-hidden rounded-lg border border-brand-line shadow-[0_18px_50px_rgba(11,27,61,0.12)]">
                <img
                  src={IMAGES.heroSurgery}
                  alt="ENT surgeon performing an advanced endoscopic procedure in a modular operation theatre"
                  className="h-[420px] w-full object-cover lg:h-[520px]"
                  loading="eager"
                />
              </div>
              <div className="absolute -bottom-5 left-5 flex items-center gap-3 rounded-lg border border-brand-line bg-white p-4 shadow-[0_10px_30px_rgba(11,27,61,0.12)]">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-brand-soft text-brand-primary">
                  <ShieldCheck className="h-6 w-6" />
                </span>
                <div>
                  <div className="font-heading text-sm font-bold text-brand-ink">
                    Modular OT · Carl Zeiss · Stryker
                  </div>
                  <div className="text-xs text-brand-slate">
                    Modern surgical technology
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT DOCTOR */}
      <section className="bg-brand-bg" data-testid="about-doctor-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-24">
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-lg border border-brand-line shadow-sm">
              <img
                src={IMAGES.aboutDoctor}
                alt="Dr. Sanjay Teza, senior ENT surgeon, in the operation theatre in Varanasi"
                className="h-full min-h-[360px] w-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="About the Surgeon"
              title="Dr. Sanjay Teza — 25+ Years of ENT Surgical Excellence"
            />
            <p className="mt-5 text-base leading-relaxed text-brand-slate">
              {DOCTOR.bio}
            </p>
            <p className="mt-4 text-base leading-relaxed text-brand-slate">
              {DOCTOR.philosophy}
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-brand-line bg-white p-6">
                <GraduationCap className="h-6 w-6 text-brand-primary" />
                <h3 className="mt-3 font-heading text-base font-bold text-brand-ink">
                  Education & Training
                </h3>
                <ul className="mt-3 space-y-2">
                  {DOCTOR.education.map((e) => (
                    <li key={e} className="flex gap-2 text-sm text-brand-slate">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-action" />
                      {e}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg border border-brand-line bg-white p-6">
                <Award className="h-6 w-6 text-brand-primary" />
                <h3 className="mt-3 font-heading text-base font-bold text-brand-ink">
                  Awards & Memberships
                </h3>
                <ul className="mt-3 space-y-2">
                  {[...DOCTOR.awards, ...DOCTOR.memberships].map((e) => (
                    <li key={e} className="flex gap-2 text-sm text-brand-slate">
                      <BadgeCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-action" />
                      {e}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SURGICAL EXPERTISE */}
      <section className="bg-white" data-testid="expertise-section">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <SectionHeading
            eyebrow="Surgical Expertise"
            title="Comprehensive ENT Surgical Care"
            subtitle="From minimally invasive sinus and ear surgery to complex head and neck procedures — precise diagnosis and modern surgical technique for every patient."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PROCEDURES.map((p, i) => {
              const Icon = PROC_ICONS[p.category] || Stethoscope;
              return (
                <Link
                  key={p.slug}
                  to={`/surgery-guide/${p.slug}`}
                  data-testid={`expertise-card-${p.slug}`}
                  className="group flex h-full flex-col rounded-lg border border-brand-line bg-white p-6 transition-transform duration-200 hover:-translate-y-1 hover:border-brand-primary hover:shadow-[0_10px_30px_rgba(11,27,61,0.08)]"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-brand-soft text-brand-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="mt-2 text-xs font-semibold uppercase tracking-wider text-brand-action">
                    {p.category}
                  </span>
                  <h3 className="mt-1 font-heading text-lg font-bold text-brand-ink">
                    {p.short}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-slate">
                    {p.tagline}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary">
                    Learn more{" "}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ADVANCED EQUIPMENT */}
      <section className="bg-brand-ink" data-testid="equipment-section">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="max-w-3xl">
            <span className="inline-flex items-center rounded-full bg-white/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-soft">
              Advanced Equipment
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold leading-tight text-white sm:text-4xl">
              Surgery Backed by World-Class Technology
            </h2>
            <p className="mt-4 text-base leading-relaxed text-white/70 md:text-lg">
              Precision surgery demands precision tools. Every procedure is
              supported by modern, well-maintained surgical technology for safe,
              accurate and minimally invasive outcomes.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {EQUIPMENT.map((e) => (
              <div
                key={e.name}
                className="rounded-lg border border-white/10 bg-white/5 p-7"
                data-testid={`equipment-${e.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Microscope className="h-7 w-7 text-brand-soft" />
                <h3 className="mt-4 font-heading text-lg font-bold text-white">
                  {e.name}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-white/70">
                  {e.detail}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { src: IMAGES.microscopeSurgery, alt: "Microscopic ear surgery using an operating microscope" },
              { src: IMAGES.endoscopy, alt: "Endoscopic ENT surgery with Stryker HD imaging system" },
              { src: IMAGES.modularOT, alt: "Modular operation theatre with surgical microscope and OT light" },
            ].map((img) => (
              <div
                key={img.src}
                className="overflow-hidden rounded-lg border border-white/10"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="h-56 w-full object-cover transition-transform duration-300 hover:scale-105"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-brand-bg" data-testid="why-choose-section">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <SectionHeading
            eyebrow="Why Choose Us"
            title="A Surgeon Patients in Varanasi Trust"
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_CHOOSE.map((w, i) => {
              const Icon = WHY_ICONS[i % WHY_ICONS.length];
              return (
                <div
                  key={w.title}
                  className="flex h-full gap-4 rounded-lg border border-brand-line bg-white p-6"
                  data-testid={`why-choose-${i}`}
                >
                  <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-brand-ink">
                      {w.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-brand-slate">
                      {w.detail}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PATIENT JOURNEY */}
      <section className="bg-white" data-testid="patient-journey-section">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <SectionHeading
            eyebrow="Patient Journey"
            title="Your Path from Consultation to Recovery"
            subtitle="A clear, structured process built around accurate diagnosis, honest advice and long-term outcomes."
          />
          <div className="mt-12 grid gap-6 md:grid-cols-3 lg:grid-cols-5">
            {PATIENT_JOURNEY.map((step) => (
              <div
                key={step.step}
                className="relative rounded-lg border border-brand-line bg-brand-bg p-6"
                data-testid={`journey-step-${step.step}`}
              >
                <span className="font-heading text-3xl font-extrabold text-brand-soft">
                  {step.step}
                </span>
                <h3 className="mt-2 font-heading text-base font-bold text-brand-ink">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-slate">
                  {step.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SURGICAL GALLERY */}
      <section className="bg-brand-bg" data-testid="gallery-section">
        <div className="mx-auto max-w-7xl px-4 pt-16 sm:px-6 lg:px-8 lg:pt-24">
          <SectionHeading
            eyebrow="Inside the OT"
            title="A Look Inside Our Operation Theatre"
            subtitle="Real photographs of Dr. Sanjay Teza and the surgical team at work — modern equipment, strict sterility and precise, minimally invasive technique."
          />
        </div>
        <div
          className="mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto px-4 pb-16 sm:px-6 lg:px-8 lg:pb-24 [scrollbar-width:thin]"
          data-testid="gallery-scroller"
        >
          {GALLERY.map((g, i) => (
            <figure
              key={g.src}
              data-testid={`gallery-item-${i}`}
              className="group relative w-[300px] flex-shrink-0 snap-start overflow-hidden rounded-lg border border-brand-line bg-white shadow-sm sm:w-[380px]"
            >
              <img
                src={g.src}
                alt={g.caption}
                loading="lazy"
                className="h-64 w-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <figcaption className="border-t border-brand-line p-4 text-sm font-medium text-brand-slate">
                {g.caption}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <CTABand />
    </div>
  );
}
